import { Box, Divider, Grid, useMediaQuery, Link, Typography, type Theme } from '@mui/material';
import { ProfileAvatar } from '@care/react-component-lib';
import { Icon48BrandDaycareCenters, Icon48BrandSmb } from '@care/react-icons';
import { decode } from 'he';
import { getSearchResults_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_providerSearchResults as SEOSegmentationProviderSearchResult } from '@/__generated__/getSearchResults';
import { ServiceId } from '@/constants';
import { SxClassProps } from '@/types/common';
import ReadMore from '../ReadMore';
import ProviderAddress from './components/ProviderAddress';
import { ProviderRate } from './components/ProviderRate';
import { ProviderRating } from './components/ProviderRating';
import ProfileReview from './ProfileReview';

const classes: SxClassProps = {
  dayCareIcon: (theme: Theme) => ({
    color: theme.palette.care.white,
    '& circle': {
      fill: theme.palette.care.white,
    },
  }),
  images: (theme: Theme) => ({
    '& img': {
      objectFit: 'contain',
      backgroundColor: theme.palette.care.white,
    },
  }),
};

interface ProviderSearchResultProps {
  seoSegmentationProvider: SEOSegmentationProviderSearchResult['seoSegmentationProvider'];
  providerReviews: SEOSegmentationProviderSearchResult['providerReviews'];
  isClientSearchResultsQuery: boolean;
  serviceId: string;
  url: SEOSegmentationProviderSearchResult['profileURL'];
}

const ProviderSearchResult = ({
  seoSegmentationProvider,
  isClientSearchResultsQuery,
  providerReviews,
  serviceId,
  url,
}: ProviderSearchResultProps) => {
  const isMobileOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const {
    provider: { address, name, description, avgReviewRating },
    numberOfReviews,
    costimate,
    providerRate,
  } = seoSegmentationProvider;

  const providerRateOrCostimate = providerRate ?? costimate;
  const rateType = providerRateOrCostimate && providerRate ? 'PROVIDER_RATE' : 'COSTIMATE';

  const urlThumbnail = seoSegmentationProvider.provider.logo?.urlThumbnail;

  const profileAvatarProps = {
    alt: name ?? undefined,
    size: isMobileOrSmaller ? 'xLarge' : 'squareSmall',
    src: urlThumbnail ?? undefined,
    slotProps: {
      avatar: {
        imgProps: isClientSearchResultsQuery
          ? { loading: 'eager' as const }
          : { loading: 'lazy' as const },
      },
    },
    'data-testid': 'dayCareIcon',
  } as const;

  const smbPlaceholderIcon =
    serviceId === ServiceId.CHILDCARE ? (
      <Icon48BrandDaycareCenters sx={classes.dayCareIcon} />
    ) : (
      <Icon48BrandSmb sx={classes.dayCareIcon} />
    );

  const targetValue = isMobileOrSmaller ? '' : '_blank';

  if (url === null) return null;
  return (
    <Box
      data-testid="providerSearchResult"
      mb={{ xs: 3, sm: 3 }}
      px={{ xs: 0, sm: 0 }}
      component="div">
      <Grid
        container
        justifyContent="space-between"
        spacing={isMobileOrSmaller ? 2 : 4}
        mb={{ xs: 1, sm: 2 }}>
        <Grid container item sm={9} spacing={2}>
          <Grid item>
            <Link variant="h3" underline="none" href={url} target={targetValue}>
              <ProfileAvatar {...profileAvatarProps} sx={classes.images}>
                {smbPlaceholderIcon}
              </ProfileAvatar>
            </Link>
          </Grid>
          <Grid item xs>
            <Typography variant="h3">
              <Link underline="none" href={url} target={targetValue} fontSize="1.125em">
                {name}
              </Link>
            </Typography>
            {address && <ProviderAddress address={address} />}
            {providerRateOrCostimate && (
              <ProviderRate providerRate={providerRateOrCostimate} rateType={rateType} />
            )}
          </Grid>
        </Grid>
        {numberOfReviews > 0 && avgReviewRating !== null && avgReviewRating > 0 && (
          <Grid item container xs={12} sm={3}>
            <ProviderRating avgReviewRating={avgReviewRating} numberOfReviews={numberOfReviews} />
          </Grid>
        )}
      </Grid>
      {description && (
        <Grid container spacing={isMobileOrSmaller ? 2 : 4}>
          <Grid item>
            <ReadMore
              gutterBottom
              color="textPrimary"
              component="div"
              variant="body2"
              charLimit={isMobileOrSmaller ? 210 : 300}
              value={decode(description.replace(/<br\s*[/]?>/gi, '\n')) || ''}
              showMoreLabel="show more"
              onClick={(e: { preventDefault: () => any }) => e.preventDefault()}
            />
          </Grid>
        </Grid>
      )}
      {providerReviews.length > 0 && <ProfileReview reviews={providerReviews} />}
      <Box mt={{ xs: 3, sm: 4 }} mx={-2}>
        <Divider />
      </Box>
    </Box>
  );
};

export default ProviderSearchResult;
